import React from 'react';
import { SearchHighlight } from '../components/SearchHighlight';

/**
 * Converts URLs in text to clickable links
 * Supports http, https, ftp, and basic www. links
 */
export const convertUrlsToLinks = (text: string): React.ReactNode => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Enhanced URL regex that captures various URL formats
  const urlRegex = /((?:https?|ftp):\/\/[^\s<>"{}|\\^`[\]]+|(?:www\.)[^\s<>"{}|\\^`[\]]+)/gi;
  
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      // Reset regex lastIndex since we're reusing it
      urlRegex.lastIndex = 0;
      
      // Add https:// prefix if it starts with www. and doesn't have a protocol
      let href = part;
      if (part.startsWith('www.') && !part.startsWith('http')) {
        href = `https://${part}`;
      }
      
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--app-primary)',
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
          onClick={(e) => e.stopPropagation()} // Prevent parent element click handlers
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/**
 * Component wrapper for converting URLs in text to clickable links
 * Preserves all other text formatting and styling
 */
interface ClickableTextProps {
  children: string;
  className?: string;
  style?: React.CSSProperties;
}

export const ClickableText: React.FC<ClickableTextProps> = ({ 
  children, 
  className, 
  style 
}) => {
  return (
    <span className={className} style={style}>
      {convertUrlsToLinks(children)}
    </span>
  );
};

/**
 * Component that combines SearchHighlight with clickable links
 * First applies search highlighting, then makes URLs clickable
 */
interface SearchHighlightWithLinksProps {
  text: string;
  searchTerm: string;
  className?: string;
  style?: React.CSSProperties;
}

export const SearchHighlightWithLinks: React.FC<SearchHighlightWithLinksProps> = ({
  text,
  searchTerm,
  className,
  style
}) => {
  // If no search term, just use clickable links
  if (!searchTerm || !searchTerm.trim()) {
    return (
      <span className={className} style={style}>
        {convertUrlsToLinks(text)}
      </span>
    );
  }

  // For search highlighting with links, we need a more complex approach
  // First, let's apply URL conversion, then search highlighting
  const urlRegex = /((?:https?|ftp):\/\/[^\s<>"{}|\\^`[\]]+|(?:www\.)[^\s<>"{}|\\^`[\]]+)/gi;
  const parts = text.split(urlRegex);
  
  return (
    <span className={className} style={style}>
      {parts.map((part, index) => {
        if (urlRegex.test(part)) {
          // Reset regex lastIndex since we're reusing it
          urlRegex.lastIndex = 0;
          
          // Add https:// prefix if it starts with www. and doesn't have a protocol
          let href = part;
          if (part.startsWith('www.') && !part.startsWith('http')) {
            href = `https://${part}`;
          }
          
          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--app-primary)',
                textDecoration: 'underline',
                wordBreak: 'break-all'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <SearchHighlight text={part} searchTerm={searchTerm} />
            </a>
          );
        }
        
        // Apply search highlighting to non-URL text
        return <SearchHighlight key={index} text={part} searchTerm={searchTerm} />;
      })}
    </span>
  );
};